import { ADDRESS_TRIM_REGEX } from 'constants';
import {
  PII_PROFILE_RESIDENTIAL_ADDRESS_FINDER_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_POST_CODE_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_STATE_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_NAME_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_NO_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_TYPE_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_SUBURB_NAME,
  PII_PROFILE_RESIDENTIAL_ADDRESS_UNITNAME_NAME,
} from 'features/pii/const';
import { address } from 'helpers';
import { Fade } from 'helpers/animation';
import useFormInput from 'hooks/form/useFormInput';
import useLang from 'hooks/useLang';
import Form from 'react-bootstrap/Form';
import styled from 'styled-components';
import { paragraph500Mixin } from 'styles';

const FormInputValidation = ({
  ref,
  field,
  information,
  setInformation,
  validation,
  setValidation,
  checkFormInputValidation,
}) => {
  const { t } = useLang();

  const {
    resetFormInput,
    setInformationFormInput,
    setValidationFormInput,
    getValueTrimAll,
    FormLabel,
    FormControlFeedback,
  } = useFormInput(field);

  const isTextColorDisabled = [
    PII_PROFILE_RESIDENTIAL_ADDRESS_SUBURB_NAME,
    PII_PROFILE_RESIDENTIAL_ADDRESS_STATE_NAME,
  ].includes(field.name);

  const onReset = () => {
    resetFormInput({
      information,
      setInformation,
      validation,
      setValidation,
    });
  };

  const handleOnChange = (e) => {
    const { value, name } = e.target;

    if (!value) {
      onReset();
      return;
    }

    let newInformation = setInformationFormInput({
      value,
      information,
      setInformation,
    });
    newInformation = {
      ...newInformation,
      [PII_PROFILE_RESIDENTIAL_ADDRESS_FINDER_NAME]: address.addressCombine(
        getValueTrimAll(
          newInformation[PII_PROFILE_RESIDENTIAL_ADDRESS_UNITNAME_NAME],
          true,
          ADDRESS_TRIM_REGEX
        ),
        getValueTrimAll(
          newInformation[PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_NO_NAME],
          true,
          ADDRESS_TRIM_REGEX
        ),
        getValueTrimAll(
          newInformation[PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_NAME_NAME],
          true,
          ADDRESS_TRIM_REGEX
        ),
        newInformation[PII_PROFILE_RESIDENTIAL_ADDRESS_STREET_TYPE_NAME]?.key,
        newInformation[PII_PROFILE_RESIDENTIAL_ADDRESS_SUBURB_NAME],
        newInformation[PII_PROFILE_RESIDENTIAL_ADDRESS_STATE_NAME],
        newInformation[PII_PROFILE_RESIDENTIAL_ADDRESS_POST_CODE_NAME]
      ),
    };
    setInformation(newInformation);

    setValidationFormInput({
      validation,
      setValidation,
      checkFormInputValidation: () =>
        checkFormInputValidation(
          newInformation[name],
          field,
          newInformation,
          validation
        ),
    });
  };
  const handleOnBlur = (e) => {
    const { value } = e.target;

    if (!value) return;
    setInformationFormInput({
      value: getValueTrimAll(value, true, ADDRESS_TRIM_REGEX),
      information,
      setInformation,
    });
  };

  return (
    <FormGroupStyled>
      <FormLabel styles={{ marginBottom: '4px' }} />
      <FormControl
        ref={ref}
        type={field.type}
        name={field.name}
        value={information ? information[field.name] : ''}
        placeholder={t(field.placeholder)}
        isInvalid={validation[field.name]}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        readOnly={field?.isReadOnly}
        $isTextColorDisabled={isTextColorDisabled}
      />
      <FormControlFeedback
        value={t(validation[field.name])}
        validationStyles={{ position: 'unset' }}
      />
    </FormGroupStyled>
  );
};

const FormGroupStyled = styled(Form.Group)`
  ${Fade};
  position: relative;
  width: 100%;
`;

const FormControl = styled(Form.Control)`
  ${paragraph500Mixin};
  text-transform: uppercase;
  height: 44px;
  border: 1px solid var(--ds-c-grey-light);
  border-radius: 8px;
  padding-inline: 10px;
  background: var(--ds-c-white);
  color: ${(props) =>
    props.$isTextColorDisabled
      ? 'var(--ds-c-grey-neutral)'
      : 'var(--ds-c-grey-dark)'};
  background: ${(props) =>
    props.$isTextColorDisabled
      ? 'var(--ds-c-grey-disabled)'
      : 'var(--ds-c-white)'};
  cursor: ${(props) => props.$isTextColorDisabled && 'not-allowed'};

  &:focus {
    background: ${(props) =>
      props.$isTextColorDisabled && 'var(--ds-c-grey-disabled)!important'};
    border: 1px solid var(--ds-c-grey-dark) !important;
    outline: none !important;
    box-shadow: none !important;
  }

  &:invalid,
  &:invalid:focus {
  }
  &.is-invalid {
    border: 1px solid var(--ds-c-red) !important;
    outline: none !important;
    box-shadow: none !important;
    background-image: none !important;
    padding-inline: 10px !important;
  }

  &[readonly] {
    background: ${(props) =>
      props.$isTextColorDisabled && 'var(--ds-c-grey-disabled)!important'};
    border: none !important;
  }

  &::placeholder {
    ${paragraph500Mixin};
    color: var(--ds-c-blue-disabled) !important;
    text-transform: uppercase;
  }
`;

export default FormInputValidation;
